/**
 * The hold-still work at a `useObjects` target (10 §3): cracking the safe, fitting the wiretap,
 * throwing a breaker. It follows the pattern of first aid and wire cutting - `Ranger.action`
 * is `'useObject'` while it runs, `movement.ts` cancels it the moment he moves, a hit knocks it
 * out of his hands - and `ranger/actions.ts` ticks it from its own timed-action chain.
 */

import type { Ctx } from '../ctx';
import { OBJECTIVES } from '../tuning';
import { propDef, propDestroyed, propDistance } from './common';
import { usableTarget, useActionTicks } from './kindsMulti';

/** The object has been worked: it counts from now on, whatever happens to it afterwards. */
export function markUsed(ctx: Ctx, propId: number): void {
  const o = ctx.world.objective;
  if (!o.usedPropIds.includes(propId)) o.usedPropIds.push(propId);
  o.usingPropId = -1;
}

function stop(ctx: Ctx): void {
  const r = ctx.world.ranger;
  r.action = 'none';
  r.actionTicks = 0;
  ctx.world.objective.usingPropId = -1;
}

/** One tick of the work; called while `ranger.action === 'useObject'`. */
export function stepUseAction(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const id = w.objective.usingPropId;
  const prop = propDef(statics.def, id);
  // The thing was blown up under his hands, he has somehow been moved off it - or what he came for has just
  // been lost (the plans burned while he was at the safe): `usableTarget` no longer offers it.
  const gone = !prop || propDestroyed(w, id) || propDistance(prop, r.x, r.z) > OBJECTIVES.useReach + 0.5;
  if (gone || (w.objective.failed && usableTarget(ctx)?.id !== id)) {
    stop(ctx);
    return;
  }
  r.actionTicks++;
  if (r.actionTicks < useActionTicks(statics.def)) return;
  markUsed(ctx, id);
  stop(ctx);
}

/** A cancelled action (he moved, he was hit) leaves no object "in use". */
export function tidyUseAction(ctx: Ctx): void {
  if (ctx.world.ranger.action !== 'useObject') ctx.world.objective.usingPropId = -1;
}
